import React from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { CSSTransition } from 'react-transition-group';
import { MenuSubLink, MenuSubLinkDataProps } from './MenuSubLink';
import { ChevronIcon, Directions } from '../../../ChevronIcon';
import {
  chevronIconEnterDelay,
  chevronIconEnterDuration,
  chevronIconEnterTimeOut,
  chevronIconExitDuration,
  chevronIconExitTimeOut,
  subLinkEnterDuration,
  subLinkEnterTimeOut,
  subLinksTotalDelay,
} from './menuLinks.utils';
import { VrLineHeight } from '../../../../../../../theming/verticalRhythm/verticalRhythm';
import { generateLinkStyleFromTheme, TextLinkComponent } from '../../../../../../partials/link/TextLink';
import { useTheme } from '../../../../../../../theming/useTheme';
import { MEDIA_BREAKPOINTS } from '../../../../../../../mediaQueries.const';
import { H4 } from '../../../../../../../typography/semanticTags';


const componentConfigThemeKey = 'menuLink';

export interface MenuLinkDataProps {
  href: string,
  text: string,
  childrenLinks: MenuSubLinkDataProps[]
}

export interface MenuLinkThemeProps {
  vrLineHeight: VrLineHeight
  idleColor: string,
  activeStateColor: string,
}

interface MenuLinkLogicProps {
  index: number,
  onClick: (index: number) => void,
  isLinkOpen: boolean
  startChevronIconAnimation: boolean
}

const getMenuLinkStyle = (vrLineHeight: VrLineHeight, linkColor: string) => StyleSheet.create({
  style: {
    color: linkColor,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      margin: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px`,
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      margin: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px`,
    },
  },
  enter: {
    opacity: 0,
  },
  enterActive: {
    opacity: 1,
    transition: `opacity ${chevronIconEnterDuration}ms ease-out`,
    transitionDelay: `${chevronIconEnterDelay}ms`,
  },
  exit: {
    opacity: 1,
  },
  exitActive: {
    opacity: 0,
    transition: `opacity ${chevronIconExitDuration}ms ease-in`,
  },
  chevronIcon: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row-reverse',
  },
});

const getSubLinkStyle = (index: number, numberOfSubLinks: number, currentSubLinkDelay: number) => {
  return StyleSheet.create({
    enter: {
      opacity: 0,
    },
    enterActive: {
      opacity: 1,
      transition: `opacity ${subLinkEnterDuration}ms ease-out`,
      transitionDelay: `${currentSubLinkDelay * index}ms`,
    },
  });
};

const createMenuSubLinks = (numberOfSubLinks: number, isLinkOpen: boolean) => (link: { href: string, text: string, }, index: number) => {
  const style = getSubLinkStyle(index, numberOfSubLinks, subLinksTotalDelay / numberOfSubLinks);
  return (
    <CSSTransition
      key={index}
      in={isLinkOpen}
      unmountOnExit
      mountOnEnter
      timeout={{
        enter: subLinkEnterTimeOut,
        exit: 0,
      }}
      classNames={{
        enter: css(style.enter),
        enterActive: css(style.enterActive),
      }}
    >
      <MenuSubLink href={link.href} text={link.text} />
    </CSSTransition>
  );
};

export const MenuLinkComponent: React.FunctionComponent<MenuLinkThemeProps & MenuLinkDataProps & MenuLinkLogicProps> = props => {
  const { href, text, onClick, index, idleColor, activeStateColor, isLinkOpen, vrLineHeight, childrenLinks, startChevronIconAnimation } = props;
  const linkColor = isLinkOpen ? activeStateColor : idleColor;
  const linkStyle = generateLinkStyleFromTheme({ idleColor: linkColor });
  const style = getMenuLinkStyle(vrLineHeight, linkColor);
  return (
    <React.Fragment>
      <li>
        <div className={css(style.style)}>
          <TextLinkComponent href={href} linkStyle={linkStyle}><H4>{text}</H4></TextLinkComponent>
          {childrenLinks.length !== 0 ? (
            <div className={css(style.chevronIcon)} onClick={() => onClick(index)}>
              <CSSTransition
                in={startChevronIconAnimation}
                unmountOnExit
                mountOnEnter
                timeout={{
                  enter: chevronIconEnterTimeOut,
                  exit: chevronIconExitTimeOut,
                }}
                classNames={{
                  enter: css(style.enter),
                  enterActive: css(style.enterActive),
                  exit: css(style.exit),
                  exitActive: css(style.exitActive),
                }}
              >
                <ChevronIcon direction={isLinkOpen ? Directions.UP : Directions.DOWN} color={linkColor} />
              </CSSTransition>
            </div>
          ) : null}
        </div>
        <ul>
          {childrenLinks.map(createMenuSubLinks(childrenLinks.length, isLinkOpen))}
        </ul>
      </li>
    </React.Fragment>
  );
};

const mapThemeProps = (theme: MenuLinkThemeProps) => {
  return {
    vrLineHeight: theme.vrLineHeight,
    idleColor: theme.idleColor,
    activeStateColor: theme.activeStateColor,
  };
};

export const MenuLink = (props: MenuLinkDataProps & MenuLinkLogicProps) => {
  const themeContext = useTheme(mapThemeProps, componentConfigThemeKey);
  return <MenuLinkComponent {...themeContext} {...props} />;
};
